// Helper hooks for content-related operations

import { getContent } from '@/api/content-api';
import { listActiveProcessRuns } from '@/api/processes-api';
import type { AsyncTask } from '@/store/asyncStore';
import { useAsyncStore } from '@/store/asyncStore';
import { deriveProgress } from '@/utils/content-utils';
import type { ProcessRunStatus } from "@vivport/backend/types";
import { Content } from "@vivport/backend/types";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { contentKeys, useContentDetail } from "./useContentQueries";
import { memberExternalAccountKeys } from './useMemberExternalAccountQueries';
import { billingKeys } from './useBillingQueries';

type ContentWithParentAnchor = Extract<Content, { parentAnchorId: number; parentAnchorTitle: string }>;

const hasParentAnchor = (item: Content): item is ContentWithParentAnchor => {
  return 'parentAnchorId' in item && 'parentAnchorTitle' in item;
};

export const useParentAnchorTitle = (item: Content): string => {
  const parentAnchorId = hasParentAnchor(item) ? item.parentAnchorId : 0;
  const { data: parentAnchor } = useContentDetail(parentAnchorId);
  return hasParentAnchor(item) ? (parentAnchor?.title || item.parentAnchorTitle) : '';
}

/**
 * Hook to get the display title for a content item
 * Uses parentAnchorTitle + code pattern for items with parent anchors.
 * Throws when no title can be derived.
 */
export const useContentDisplayTitle = (content: Content): string => {
  const parentAnchorTitle = useParentAnchorTitle(content);
  
  return useMemo(() => {
    if (content.contentType === 'MESSAGE') {
      const subject = content.subject?.trim() ?? '';
      if (subject.length > 0) {
        return subject;
      }
      const snippet = content.snippet?.trim() ?? '';
      if (snippet.length > 0) {
        return snippet;
      }
      const title = content.title?.trim() ?? '';
      if (title.length > 0) {
        return title;
      }
      return `Message ${content.code || content.id}`;
    }

    // Check if content has parentAnchorTitle (all types except ANCHOR)
    const hasParentAnchorTitle = 
      content.contentType !== 'ANCHOR' && 
      (parentAnchorTitle || ('parentAnchorTitle' in content && content.parentAnchorTitle));

    if (hasParentAnchorTitle && content.code) {
      const titleToUse = parentAnchorTitle || ('parentAnchorTitle' in content ? content.parentAnchorTitle : '');
      return `${titleToUse} ${content.code}`;
    }

    if (content.title) {
      return content.title;
    }

    if (content.contentType === 'NEO_CHAT') {
      return `Neo Chat ${content.code}`;
    }

    throw new Error(`Unable to derive display title for content ${content.code}`);
  }, [content, parentAnchorTitle]);
}


interface ContentAsyncTaskWatcherUpdaters {
  photo?: { 
    updateLocalByTempId?: (tempId: string, u: any) => void; 
    updateByRemoteId?: (id: number, u: any) => void; 
  };
  recording?: { 
    updateLocalByTempId?: (tempId: string, u: any) => void; 
    updateByRemoteId?: (id: number, u: any) => void; 
  };
}

/**
 * Watches in-flight background tasks and updates progress from polled content state.
 */
export function useContentAsyncTaskWatcher(
  tasks: AsyncTask[],
  updateTask: (id: string, updates: Partial<AsyncTask>) => void,
  updaters?: ContentAsyncTaskWatcherUpdaters
) {
  const queryClient = useQueryClient();
  const handledCompletedTasksRef = useRef<Set<string>>(new Set());
  const task = [...tasks].reverse().find((t) => t.status === 'processing' && t.payload);

  const contentId = useMemo(() => {
    if (!task?.payload) {
      return null;
    }

    if (typeof task.contentId === 'number') {
      return task.contentId;
    }

    const payload = task.payload;
    if (typeof payload.remoteContentId === 'number') {
      return payload.remoteContentId;
    }
    const idFields = ['photoId', 'recordingId', 'anchorId', 'eventId', 'documentId'];
    for (const field of idFields) {
      if (typeof payload[field] === 'number') {
        return payload[field];
      }
    }
    
    return null;
  }, [task]);

  const detailQuery = useQuery({
    queryKey: contentId ? contentKeys.detail(contentId) : ['content', 'idle'],
    queryFn: () => getContent(contentId!),
    enabled: !!contentId && task?.status === 'processing',
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  // Derive progress from content state
  const { progress, status, description } = useMemo(() => {
    if (!detailQuery.data) {
      return { progress: 0, status: 'pending' as const, description: 'Starting...' };
    }
    return deriveProgress(detailQuery.data, task?.contentId?.toString());
  }, [detailQuery.data, task?.contentId]);

  useEffect(() => {
    if (
      task &&
      status === 'processing' &&
      (task.progress !== progress || task.description !== description)
    ) {
      updateTask(task.id, { progress, description });
    }
  }, [task, progress, description, status, updateTask]);

  useEffect(() => {
    if (task && status === 'completed' && contentId && detailQuery.data) {
      if (handledCompletedTasksRef.current.has(task.id)) {
        return;
      }
      handledCompletedTasksRef.current.add(task.id);

      if (updaters) {
        const contentType = task.contentType?.toLowerCase();
        const data = detailQuery.data;

        if (contentType === 'photo' && updaters.photo?.updateByRemoteId) {
          const updateData = {
            ...data,
            title: data.title,
            description: data.description,
          };
          updaters.photo.updateByRemoteId(contentId, updateData);
        } else if (contentType === 'recording' && updaters.recording?.updateByRemoteId) {
          const updateData = {
            ...data,
            description: data.description,
          };
          updaters.recording.updateByRemoteId(contentId, updateData);
        }
      }

      if (task.status !== 'completed' || task.progress !== 100 || task.description !== description) {
        updateTask(task.id, { status: 'completed', progress: 100, description });
      }

      queryClient.setQueryData(contentKeys.detail(contentId), detailQuery.data);
      queryClient.setQueryData(['content-ref', String(contentId)], detailQuery.data);
      if (typeof detailQuery.data.code === 'string' && detailQuery.data.code.length > 0) {
        queryClient.setQueryData(['content-ref', detailQuery.data.code], detailQuery.data);
      }

      queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['content-ref'], exact: false, refetchType: 'all' });

      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
    }
  }, [status, task, updateTask, description, queryClient, contentId, updaters, detailQuery.data]);

  useEffect(() => {
    if (task && detailQuery.error && task.status !== 'failed') {
      updateTask(task.id, { status: 'failed', description: 'Processing failed' });
    }
  }, [detailQuery.error, task, updateTask]);
}

function mapRunStatusToTaskStatus(status: ProcessRunStatus): AsyncTask['status'] {
  switch (status) {
    case 'QUEUED':
    case 'WAITING_APPROVAL':
      return 'pending';
    case 'RUNNING':
      return 'processing';
    case 'SUCCEEDED':
    case 'SKIPPED':
      return 'completed';
    case 'FAILED':
    case 'DEAD_LETTER':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
  }
}

export function useActiveProcessRunsWatcher() {
  const queryClient = useQueryClient();
  const handledTerminalRef = useRef<Set<number>>(new Set());
  const seenSequenceRef = useRef<Map<number, number>>(new Map());

  const { data } = useQuery({
    queryKey: ['process-runs', 'active'],
    queryFn: listActiveProcessRuns,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const store = useAsyncStore.getState();
    for (const run of data.items) {
      if (handledTerminalRef.current.has(run.id)) {
        continue;
      }
      const taskStatus = mapRunStatusToTaskStatus(run.status);
      const isTerminal = taskStatus === 'completed' || taskStatus === 'failed' || taskStatus === 'cancelled';
      const existing = store.tasks.find(
        (t) => t.contentType === 'PROCESS_RUN' && Number(t.contentId) === run.id
      );

      if (existing) {
        store.updateTask(existing.id, {
          status: taskStatus,
          runStatus: run.status,
          description: run.inputMessage,
          ...(run.error ? { error: run.error } : {}),
        });
      } else if (!isTerminal) {
        store.addTask({
          title: run.processNameSnapshot ?? 'Background task',
          description: run.inputMessage,
          status: taskStatus,
          runStatus: run.status,
          contentType: 'PROCESS_RUN',
          contentId: run.id,
        });
      }

      const lastSeenSeq = seenSequenceRef.current.get(run.id) ?? 0;
      if (run.lastEntrySequence > lastSeenSeq) {
        seenSequenceRef.current.set(run.id, run.lastEntrySequence);
        if (!isTerminal) {
          void queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
          void queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
          // documentRender is the per-id query family Document pages render from; the
          // detail-key invalidation cannot reach it (root-keyed). Family-level, mounted-only.
          void queryClient.invalidateQueries({ queryKey: [...contentKeys.all, 'documentRender'] });
        }
      }

      if (isTerminal) {
        handledTerminalRef.current.add(run.id);
        seenSequenceRef.current.delete(run.id);
        void queryClient.invalidateQueries({ queryKey: billingKeys.spends() });
        void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
        if (run.status === 'SUCCEEDED') {
          void queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
          void queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
          void queryClient.invalidateQueries({ queryKey: [...contentKeys.all, 'documentRender'] });
          void queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.lists() });
        }
      }
    }
  }, [data, queryClient]);
}

